import React, { useState, useEffect } from "react";
import { Match } from "../types/tournament";
import { validateScore, isGameComplete } from "../utils/tournamentLogic";
import { X, AlertCircle, CheckCircle } from "lucide-react";

interface ScoreEntryProps {
  match: Match | undefined;
  onClose: () => void;
  onSubmit: (matchId: string, team1Score: number, team2Score: number) => void;
}

const ScoreEntry: React.FC<ScoreEntryProps> = ({
  match,
  onClose,
  onSubmit,
}) => {
  const [team1Score, setTeam1Score] = useState("");
  const [team2Score, setTeam2Score] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (match?.result) {
      setTeam1Score(match.result.team1Score.toString());
      setTeam2Score(match.result.team2Score.toString());
    }
  }, [match]);

  const validateScores = () => {
    const newErrors: string[] = [];
    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);

    if (!team1Score || !team2Score) {
      newErrors.push("Both scores are required");
      return newErrors;
    }

    if (!validateScore(score1)) {
      newErrors.push("Team 1 score must be between 0 and 13");
    }

    if (!validateScore(score2)) {
      newErrors.push("Team 2 score must be between 0 and 13");
    }

    if (score1 === score2 && score1 >= 11) {
      newErrors.push("Scores cannot be tied at 11 or higher");
    }

    if (!isGameComplete(score1, score2)) {
      newErrors.push(
        "Game must be completed (first to 11, win by 2, or reach 13)"
      );
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateScores();

    if (newErrors.length === 0 && match) {
      onSubmit(match.id, parseInt(team1Score), parseInt(team2Score));
      onClose();
    } else {
      setErrors(newErrors);
    }
  };

  const getWinner = () => {
    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);

    if (isGameComplete(score1, score2)) {
      return score1 > score2 ? match?.team1.name : match?.team2.name;
    }
    return null;
  };

  const winner = getWinner();

  if (!match) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Enter Match Score</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Match Info */}
        <div className="p-6 border-b border-gray-200">
          <div className="text-center mb-4">
            <p className="text-lg font-semibold text-gray-900">
              Court {match.court}
            </p>
            <p className="text-sm text-gray-600">Round {match.round}</p>
          </div>

          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-semibold text-gray-900">{match.team1.name}</p>
              <p className="text-sm text-gray-600">
                {match.team1.player1} & {match.team1.player2}
              </p>
            </div>

            <div className="text-center text-gray-500 font-medium">VS</div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-semibold text-gray-900">{match.team2.name}</p>
              <p className="text-sm text-gray-600">
                {match.team2.player1} & {match.team2.player2}
              </p>
            </div>
          </div>
        </div>

        {/* Score Entry Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {match.team1.name} Score
              </label>
              <input
                type="number"
                min="0"
                max="13"
                value={team1Score}
                onChange={(e) => setTeam1Score(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-13"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {match.team2.name} Score
              </label>
              <input
                type="number"
                min="0"
                max="13"
                value={team2Score}
                onChange={(e) => setTeam2Score(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-13"
              />
            </div>

            {/* Winner Preview */}
            {winner && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-3">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-success-600 mr-2" />
                  <span className="text-success-800 font-medium">
                    Winner: {winner}
                  </span>
                </div>
              </div>
            )}

            {/* Error Messages */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    {errors.map((error, index) => (
                      <p key={index} className="text-red-800 text-sm">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Scoring Rules Reminder */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm">
                <strong>Scoring Rules:</strong> First to 11, win by 2, maximum
                13 points
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Save Score
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScoreEntry;
